// Parse + auto-map helpers for the contacts spreadsheet importer.
// Pure functions only: no React, no Supabase, easy to unit-test.
// Mirrors bookingsImportParse.ts; contacts are simpler (name/email/phone/notes).

import { normalizeDate, normalizePhone, type ParsedRow } from "@/lib/bookingsImportParse";

export type ContactField = "name" | "email" | "phone" | "notes" | "last_booking_date";

// Name is required. Email is optional, but each row needs an email OR a phone
// to identify the person (the server checks that row by row), so at least one
// of the two columns has to be mapped.
export const REQUIRED_FIELDS: ContactField[] = ["name"];
export const IDENTITY_FIELDS: ContactField[] = ["email", "phone"];

export const ALL_FIELDS: { key: ContactField; label: string; required: boolean }[] = [
  { key: "name", label: "Name", required: true },
  { key: "email", label: "Email", required: false },
  { key: "phone", label: "Phone", required: false },
  { key: "notes", label: "Notes", required: false },
  { key: "last_booking_date", label: "Last booking date", required: false },
];

/** Labels of what still has to be mapped before validating, e.g. ["Email or phone"]. */
export function missingImportFields(mapping: Record<ContactField, string | null>): string[] {
  const missing = REQUIRED_FIELDS
    .filter((field) => !mapping[field])
    .map((field) => ALL_FIELDS.find((item) => item.key === field)?.label ?? field);
  if (!IDENTITY_FIELDS.some((field) => mapping[field])) missing.push("Email or phone");
  return missing;
}

// Header synonyms used for auto-detect. All lowercased, punctuation stripped.
const HEADER_HINTS: Record<ContactField, string[]> = {
  name: ["name", "customer", "customer name", "client", "client name", "contact", "contact name", "full name", "first name"],
  email: ["email", "customer email", "client email", "e-mail", "mail", "email address"],
  phone: ["phone", "mobile", "customer phone", "client phone", "tel", "telephone", "whatsapp", "phone number", "contact number"],
  notes: ["notes", "remark", "remarks", "comment", "comments", "description", "tags"],
  last_booking_date: ["last booking date", "last booking", "last booked", "last visit", "last visit date", "last session", "last class", "last appointment", "last seen", "last purchase"],
};

const normalize = (s: string) =>
  s.toLowerCase().replace(/[_\-/\\.]+/g, " ").replace(/\s+/g, " ").trim();

// Given the spreadsheet's header row, propose a best-guess mapping of
// ContactField → header name (or null when no good match).
export function autoMapHeaders(
  headers: string[],
): Record<ContactField, string | null> {
  const used = new Set<string>();
  const out = {} as Record<ContactField, string | null>;
  const normHeaders = headers.map((h) => ({ raw: h, norm: normalize(h) }));

  for (const field of ALL_FIELDS) {
    const hints = HEADER_HINTS[field.key];
    let best: string | null = null;
    let bestScore = 0;

    for (const h of normHeaders) {
      if (used.has(h.raw)) continue;
      if (hints.includes(h.norm)) {
        best = h.raw;
        bestScore = 100;
        break;
      }
      for (const hint of hints) {
        if (h.norm.includes(hint) || hint.includes(h.norm)) {
          const score = Math.min(h.norm.length, hint.length);
          if (score > bestScore) {
            best = h.raw;
            bestScore = score;
          }
        }
      }
    }

    if (best) used.add(best);
    out[field.key] = best;
  }
  return out;
}

export type { ParsedRow };

// Apply the user's column mapping + per-field normalization to produce the
// payload sent to the contacts-import edge function.
export function applyMapping(
  rows: ParsedRow[],
  mapping: Partial<Record<ContactField, string | null>>,
) {
  return rows.map((row, i) => {
    const pick = (k: ContactField) => {
      const col = mapping[k];
      return col ? row[col] : null;
    };
    return {
      row_index: i + 2, // +2 because spreadsheet row 1 is the header
      name: (pick("name") ?? "").toString().trim() || null,
      email: (pick("email") ?? "").toString().trim() || null,
      phone: normalizePhone(pick("phone")),
      notes: (pick("notes") ?? "").toString().trim() || null,
      last_booking_date: normalizeDate(pick("last_booking_date")),
    };
  });
}
